import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, TrendingUp, Dumbbell, Crown, Gift, Calendar, Plus, Download, Database, CheckCircle, KeyRound, Clock, FileDown, Image, DollarSign, Building2, Ban, Power, UtensilsCrossed } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import HamburgerMenu from '../components/HamburgerMenu';
import AdminPayments from './admin/AdminPayments';
import AdminOrganizations from './admin/AdminOrganizations';
import AdminMeals from './admin/AdminMeals';

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [exercises, setExercises] = useState([]);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all'); // New: filter by difficulty
  const [fetchingExercises, setFetchingExercises] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deletingUsers, setDeletingUsers] = useState(false);
  
  // Data Export State
  const [mongodbUri, setMongodbUri] = useState('');
  const [targetDbName, setTargetDbName] = useState('anvilash_production');
  const [exporting, setExporting] = useState(false);
  const [exportResults, setExportResults] = useState(null);
  
  // Password Reset Requests State
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [approvalPassword, setApprovalPassword] = useState('');
  const [approvingRequest, setApprovingRequest] = useState(null);

  // CSV Download State
  const [csvExportUser, setCsvExportUser] = useState('all');
  const [csvDataType, setCsvDataType] = useState('all');
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load stats
      const statsResponse = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsResponse.data);

      // Load all users
      const usersResponse = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(usersResponse.data);
      
      // Load password reset requests
      try {
        const resetResponse = await axios.get(`${API}/admin/password-reset-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPasswordResetRequests(resetResponse.data);
      } catch (e) {
        // Endpoint might not exist yet
        console.log('Password reset requests endpoint not available');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Admin data error:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
      setLoading(false);
    }
  };

  const handleApprovePasswordReset = async (requestId) => {
    if (!approvalPassword || approvalPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/approve-password-reset`, {
        request_id: requestId,
        new_password: approvalPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password reset approved!');
      setApprovingRequest(null);
      setApprovalPassword('');
      loadAdminData(); // Reload to update the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve reset');
    }
  };

  const handleRejectPasswordReset = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reject-password-reset/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password reset rejected');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject reset');
    }
  };

  const handleExtendPremium = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/extend-premium/${userId}?days=${extendDays}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Extended premium by ${extendDays} days!`);
      loadAdminData(); // Reload data
      setSelectedUser(null);
    } catch (error) {
      console.error('Extend premium error:', error);
      toast.error('Failed to extend premium');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/reset-user-password`,
        {
          user_id: userId,
          new_password: newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Password reset successfully!');
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Failed to reset password');
    }
  };

  const handleToggleActive = async (user) => {
    const disable = !user.disabled;
    if (disable && !window.confirm(`Disable ${user.email}? They will be signed out and unable to log in.`)) return;
    try {
      await axios.post(`${API}/admin/users/${user.id}/toggle-active`, { disabled: disable });
      toast.success(`Account ${disable ? 'disabled' : 'enabled'} for ${user.email}`);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, disabled: disable } : u)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account status');
    }
  };

  const fetchAllExercises = async () => {
    try {
      setFetchingExercises(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exercises/fetch-all-sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Fetched ${response.data.total_exercises} exercises from all sources!`);
      loadExercises(); // Reload exercise list
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error('Failed to fetch exercises from APIs');
    } finally {
      setFetchingExercises(false);
    }
  };

  const importYogaPilates = async () => {
    try {
      setFetchingExercises(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/exercises/import-yoga-pilates`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Imported ${response.data.total_imported} yoga/pilates/stretching exercises!`);
      loadExercises(); // Reload exercise list
    } catch (error) {
      console.error('Error importing yoga/pilates:', error);
      toast.error('Failed to import yoga/pilates exercises');
    } finally {
      setFetchingExercises(false);
    }
  };

  const loadExercises = async () => {
    try {
      const token = localStorage.getItem('token');
      // Load ALL exercises with cache-busting parameter to bypass any caching
      const cacheBuster = new Date().getTime();
      const url = `${API}/exercises/all?limit=2000&_nocache=${cacheBuster}`;
      console.log('[Admin] Fetching exercises from:', url);
      
      const response = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log('[Admin] Raw API response:', response.data);
      console.log('[Admin] Exercises array length:', response.data.exercises?.length || 0);
      
      const loadedExercises = response.data.exercises || [];
      setExercises(loadedExercises);
      
      // Show success message with actual counts
      const beginnerCount = loadedExercises.filter(e => e.difficulty === 'beginner').length;
      const intermediateCount = loadedExercises.filter(e => e.difficulty === 'intermediate').length;
      const advancedCount = loadedExercises.filter(e => e.difficulty === 'advanced').length;
      const expertCount = loadedExercises.filter(e => e.difficulty === 'expert').length;
      
      console.log('[Admin] Exercise counts by difficulty:', { beginner: beginnerCount, intermediate: intermediateCount, advanced: advancedCount, expert: expertCount });
      
      toast.success(`Loaded ${loadedExercises.length} exercises: ${beginnerCount} beginner, ${intermediateCount} intermediate, ${advancedCount} advanced, ${expertCount} expert`);
    } catch (error) {
      console.error('[Admin] Error loading exercises:', error);
      console.error('[Admin] Error response:', error.response?.data);
      setExercises([]);
      toast.error('Failed to load exercises');
    }
  };

  const [exerciseEditModal, setExerciseEditModal] = useState(null);
  const [exerciseEditForm, setExerciseEditForm] = useState({});

  const updateExerciseDifficulty = async (exerciseId, difficulty) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/exercises/${exerciseId}/difficulty?difficulty=${difficulty}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Exercise difficulty updated!');
      setEditingExercise(null);
      loadExercises();
    } catch (error) {
      console.error('Error updating exercise:', error);
      toast.error('Failed to update exercise');
    }
  };

  const openExerciseEditModal = (exercise) => {
    setExerciseEditModal(exercise.id);
    setExerciseEditForm({
      name: exercise.name || '',
      muscle: exercise.muscle || '',
      equipment: exercise.equipment || '',
      difficulty: exercise.difficulty || '',
      instructions: exercise.instructions || '',
      image_url: exercise.image_url || '',
      gif_url: exercise.gif_url || ''
    });
  };

  const saveExerciseEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/exercises/${exerciseEditModal}`,
        exerciseEditForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Exercise updated successfully!');
      setExerciseEditModal(null);
      setExerciseEditForm({});
      loadExercises();
    } catch (error) {
      console.error('Error updating exercise:', error);
      toast.error(error.response?.data?.detail || 'Failed to update exercise');
    }
  };

  useEffect(() => {
    if (activeTab === 'exercises' && exercises.length === 0) {
      loadExercises();
    }
  }, [activeTab]);

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }

    try {
      setDeletingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/bulk-delete-users`,
        selectedUsers,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Successfully deleted ${response.data.deleted_count} user(s)`);
      
      if (response.data.failed_count > 0) {
        toast.error(`Failed to delete ${response.data.failed_count} user(s)`);
      }
      
      setSelectedUsers([]);
      setShowBulkDeleteModal(false);
      loadAdminData();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setDeletingUsers(false);
    }
  };

  // Handle CSV data download
  const handleDownloadCsv = async () => {
    try {
      setDownloadingCsv(true);
      const token = localStorage.getItem('token');
      const url = csvExportUser === 'all'
        ? `${API}/admin/export-csv-all-users?data_type=${csvDataType}`
        : `${API}/admin/export-csv/${csvExportUser}?data_type=${csvDataType}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 120000
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename="')[1]?.replace('"', '') || 'export.csv'
        : 'export.csv';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('CSV downloaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download CSV');
    } finally {
      setDownloadingCsv(false);
    }
  };

  // Handle data export to external MongoDB
  const handleExportToMongoDB = async () => {
    if (!mongodbUri) {
      toast.error('Please enter a MongoDB connection URI');
      return;
    }

    if (!mongodbUri.startsWith('mongodb://') && !mongodbUri.startsWith('mongodb+srv://')) {
      toast.error('Invalid MongoDB URI format');
      return;
    }

    try {
      setExporting(true);
      setExportResults(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/export-to-mongodb`,
        {
          mongodb_uri: mongodbUri,
          target_db_name: targetDbName
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minute timeout for large exports
        }
      );
      
      setExportResults(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Export error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Export failed';
      toast.error(errorMsg);
      setExportResults({ status: 'error', error: errorMsg });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black">
        <HamburgerMenu />
        <div className="flex items-center justify-center pt-20">
          <div className="text-blood-400 text-xl font-bold tracking-wider">LOADING ADMIN PANEL...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black pb-20 overflow-x-hidden">
      <HamburgerMenu />
      
      <div className="max-w-7xl mx-auto px-4 py-4 animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 mb-1 tracking-wider">
            ADMIN DASHBOARD
          </h1>
          <p className="text-stone-500 uppercase tracking-widest text-xs">Anvil & Ash Management</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <Button
            onClick={() => setActiveTab('users')}
            size="sm"
            className={activeTab === 'users' 
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap' 
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
          >
            👥 Users
          </Button>
          <Button
            onClick={() => setActiveTab('payments')}
            size="sm"
            className={activeTab === 'payments'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap'
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
            data-testid="payments-tab"
          >
            <DollarSign className="w-4 h-4 mr-1 inline" /> Payments
          </Button>
          <Button
            onClick={() => setActiveTab('organizations')}
            size="sm"
            className={activeTab === 'organizations'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap'
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
            data-testid="organizations-tab"
          >
            <Building2 className="w-4 h-4 mr-1 inline" /> Organizations
          </Button>
          <Button
            onClick={() => setActiveTab('meals')}
            size="sm"
            className={activeTab === 'meals'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap'
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
            data-testid="meals-tab"
          >
            <UtensilsCrossed className="w-4 h-4 mr-1 inline" /> Meals
          </Button>
          <Button
            onClick={() => setActiveTab('exercises')}
            size="sm"
            className={activeTab === 'exercises'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap' 
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
          >
            💪 Exercises
          </Button>
          <Button
            onClick={() => setActiveTab('export')}
            size="sm"
            className={activeTab === 'export'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap' 
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
          >
            <Database className="w-4 h-4 mr-1 inline" /> Export Data
          </Button>
          <Button
            onClick={() => setActiveTab('downloads')}
            size="sm"
            className={activeTab === 'downloads'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap' 
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
            data-testid="downloads-tab"
          >
            <FileDown className="w-4 h-4 mr-1 inline" /> Downloads
          </Button>
          <Button
            onClick={() => setActiveTab('password-resets')}
            size="sm"
            className={activeTab === 'password-resets'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap' 
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap'
            }
          >
            <KeyRound className="w-4 h-4 mr-1 inline" /> Resets {passwordResetRequests.length > 0 && <span className="ml-1 bg-blood-600 text-white text-xs px-1.5 rounded-full">{passwordResetRequests.length}</span>}
          </Button>
          <Button
            onClick={() => navigate('/exercise-gallery')}
            size="sm"
            className="glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap"
            data-testid="exercise-gallery-tab"
          >
            <Image className="w-4 h-4 mr-1 inline" /> Image Gallery
          </Button>
        </div>

        {/* Stats Cards - Show for Users tab */}
        {activeTab === 'users' && stats && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            <Card className="glass p-4 border border-stone-800">
              <Users className="w-6 h-6 text-blood-500 mb-1" />
              <p className="text-stone-400 text-xs uppercase tracking-wider">Total Users</p>
              <p className="text-2xl font-bold text-blood-400">{stats.total_users}</p>
            </Card>

            <Card className="glass p-4 border border-stone-800">
              <Crown className="w-6 h-6 text-blood-500 mb-1" />
              <p className="text-stone-400 text-xs uppercase tracking-wider">Premium</p>
              <p className="text-2xl font-bold text-blood-400">{stats.premium_users}</p>
            </Card>

            <Card className="glass p-4 border border-stone-800">
              <Dumbbell className="w-6 h-6 text-blood-500 mb-1" />
              <p className="text-stone-400 text-xs uppercase tracking-wider">Workouts</p>
              <p className="text-2xl font-bold text-blood-400">{stats.completed_workouts}</p>
            </Card>

            <Card className="glass p-4 border border-stone-800">
              <Gift className="w-6 h-6 text-blood-500 mb-1" />
              <p className="text-stone-400 text-xs uppercase tracking-wider">Referrals</p>
              <p className="text-2xl font-bold text-blood-400">{stats.total_referrals}</p>
            </Card>
          </div>
        )}

        {/* Growth funnel - visitors vs signups */}
        {activeTab === 'users' && stats && (
          <div className="mb-6" data-testid="growth-funnel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <Card className="glass p-4 border border-stone-800">
                <TrendingUp className="w-6 h-6 text-emerald-400 mb-1" />
                <p className="text-stone-400 text-xs uppercase tracking-wider">Visitors Today</p>
                <p className="text-2xl font-bold text-white" data-testid="visits-today">{stats.visits_today ?? 0}</p>
              </Card>
              <Card className="glass p-4 border border-stone-800">
                <TrendingUp className="w-6 h-6 text-emerald-400 mb-1" />
                <p className="text-stone-400 text-xs uppercase tracking-wider">Visitors (7d)</p>
                <p className="text-2xl font-bold text-white">{stats.visits_7d ?? 0} <span className="text-xs text-stone-500 font-normal">({stats.unique_visitors_7d ?? 0} unique)</span></p>
              </Card>
              <Card className="glass p-4 border border-stone-800">
                <Plus className="w-6 h-6 text-blood-400 mb-1" />
                <p className="text-stone-400 text-xs uppercase tracking-wider">New Users Today</p>
                <p className="text-2xl font-bold text-blood-400" data-testid="signups-today">{stats.signups_today ?? 0}</p>
              </Card>
              <Card className="glass p-4 border border-stone-800">
                <Plus className="w-6 h-6 text-blood-400 mb-1" />
                <p className="text-stone-400 text-xs uppercase tracking-wider">New Users (7d)</p>
                <p className="text-2xl font-bold text-blood-400">{stats.signups_7d ?? 0}</p>
              </Card>
            </div>
            <Card className="glass p-4 border border-stone-800" data-testid="recent-signups-card">
              <h3 className="text-sm font-bold text-blood-400 tracking-wider uppercase mb-3">Recent Signups</h3>
              {(stats.recent_signups || []).length === 0 ? (
                <p className="text-stone-500 text-sm">No signups yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.recent_signups.map((s, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-stone-900 pb-1.5">
                      <span className="text-white font-medium min-w-[110px]">{s.username}</span>
                      <span className="text-stone-500">{s.email}</span>
                      <span className="text-stone-400 ml-auto">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 rounded border ${s.tier === 'premium' ? 'text-amber-400 border-amber-800' : 'text-stone-500 border-stone-700'}`}>{s.tier}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-stone-600 mt-3">Visitors = landing-page views (tracked from now on). New Users = completed account signups.</p>
            </Card>
          </div>
        )}

        {/* User Management - Show for Users tab */}
        {activeTab === 'users' && (
          <Card className="glass p-4 border border-stone-800">
          <div className="flex flex-col gap-3 mb-4">
            <h2 className="text-lg font-bold text-blood-400 tracking-wider">USER MANAGEMENT</h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-stone-900 border-stone-700 text-white flex-1"
              />
              {selectedUsers.length > 0 && (
                <Button
                  onClick={() => setShowBulkDeleteModal(true)}
                  size="sm"
                  className="bg-red-700 hover:bg-red-800 text-white font-bold whitespace-nowrap"
                >
                  🗑️ Delete ({selectedUsers.length})
                </Button>
              )}
            </div>
          </div>

          {/* Select All Checkbox */}
          <div className="flex items-center gap-3 mb-4 glass p-3 rounded-lg border border-stone-800">
            <input
              type="checkbox"
              checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
              onChange={handleSelectAll}
              className="w-5 h-5 cursor-pointer"
            />
            <label className="text-blood-400 font-semibold cursor-pointer text-sm" onClick={handleSelectAll}>
              Select All ({filteredUsers.length})
            </label>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredUsers.map((user) => (
              <div key={user.id} className="glass p-3 rounded-lg border border-stone-800">
                <div className="flex justify-between items-start gap-2">
                  {/* Checkbox */}
                  <div className="flex items-start pt-1">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      disabled={user.is_admin}
                      className="w-5 h-5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="text-blood-400 font-bold text-lg">{user.username}</p>
                      {user.is_admin && (
                        <span className="text-xs bg-blood-700 text-white px-2 py-1 rounded font-bold">
                          ADMIN
                        </span>
                      )}
                      {user.tier === 'premium' && (
                        <span className="text-xs bg-gradient-to-r from-blood-700 to-blood-900 text-white px-2 py-1 rounded font-bold">
                          PREMIUM
                        </span>
                      )}
                    </div>
                    <p className="text-stone-400 text-sm">{user.email}</p>
                    
                    {/* User Statistics */}
                    {user.stats && (
                      <div className="mt-3 p-3 glass rounded-lg border border-stone-800">
                        <p className="text-blood-400 text-xs font-bold mb-2 uppercase tracking-wider">Activity Stats</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                          <div>
                            <span className="text-stone-500">Total Workouts:</span>
                            <span className="text-white ml-1 font-bold">{user.stats.total_workouts}</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Home Arena:</span>
                            <span className="text-white ml-1 font-bold">{user.stats.home_workouts}</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Gym Arena:</span>
                            <span className="text-white ml-1 font-bold">{user.stats.gym_workouts}</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Days Active:</span>
                            <span className="text-white ml-1 font-bold">{user.stats.days_active}</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Avg Calories:</span>
                            <span className="text-white ml-1 font-bold">{user.stats.avg_calories_per_day}/day</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Nutrition Logs:</span>
                            <span className="text-white ml-1 font-bold">{user.stats.nutrition_logs_count}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                      <div>
                        <span className="text-stone-500">Referrals:</span>
                        <span className="text-blood-400 ml-1 font-bold">{user.referral_count || 0}</span>
                      </div>
                      <div>
                        <span className="text-stone-500">Code:</span>
                        <span className="text-blood-400 ml-1 font-bold">{user.referral_code || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-stone-500">Skill:</span>
                        <span className="text-blood-400 ml-1 font-bold">{user.skill_level || 'N/A'}</span>
                      </div>
                      {user.trial_ends_at && (
                        <div>
                          <span className="text-stone-500">Ends:</span>
                          <span className="text-blood-400 ml-1 font-bold">
                            {new Date(user.trial_ends_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setResetPasswordUser(user)}
                      className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold"
                    >
                      🔑 RESET
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedUser(user)}
                      className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      EXTEND
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(user)}
                      className={user.disabled
                        ? 'glass border-emerald-700 text-emerald-400 hover:bg-emerald-950/50 font-semibold'
                        : 'glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold'}
                      data-testid={`toggle-active-${user.id}`}
                    >
                      {user.disabled ? <><Power className="w-4 h-4 mr-1" />ENABLE</> : <><Ban className="w-4 h-4 mr-1" />DISABLE</>}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
        )}

        {activeTab === 'payments' && <AdminPayments stats={stats} users={users} />}
        {activeTab === 'organizations' && <AdminOrganizations />}
        {activeTab === 'meals' && <AdminMeals />}

        {/* Extend Premium Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <Card className="glass p-8 max-w-md w-full border border-blood-800">
              <h3 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider">
                EXTEND PREMIUM
              </h3>
              <p className="text-stone-400 mb-4">
                User: <span className="text-blood-400 font-bold">{selectedUser.username}</span>
              </p>
              <p className="text-stone-400 mb-6">
                Email: <span className="text-blood-400">{selectedUser.email}</span>
              </p>

              <div className="mb-6">
                <label className="text-blood-400 text-sm mb-2 block uppercase tracking-wider">
                  Days to Add
                </label>
                <Input
                  type="number"
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
                  className="bg-stone-900 border-stone-700 text-white"
                  min="1"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSelectedUser(null)}
                  className="flex-1 glass border-stone-700 text-stone-400 hover:bg-stone-800 font-semibold"
                >
                  CANCEL
                </Button>
                <Button
                  onClick={() => handleExtendPremium(selectedUser.id)}
                  className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
                >
                  EXTEND
                </Button>
              </div>
            </Card>
          </div>
        )}


        {/* Password Reset Modal */}
        {resetPasswordUser && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="glass p-8 max-w-md w-full border border-blood-700">
              <h3 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider">
                RESET PASSWORD
              </h3>
              <p className="text-stone-400 mb-6">
                Reset password for: <span className="text-blood-400 font-bold">{resetPasswordUser.email}</span>
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-stone-400 uppercase tracking-wider text-sm">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="glass text-white mt-2"
                    minLength={6}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => handleResetPassword(resetPasswordUser.id)}
                    disabled={!newPassword || newPassword.length < 6}
                    className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold"
                  >
                    RESET PASSWORD
                  </Button>
                  <Button
                    onClick={() => {
                      setResetPasswordUser(null);
                      setNewPassword('');
                    }}
                    variant="outline"
                    className="flex-1 glass border-stone-700 text-stone-400 hover:bg-stone-800"
                  >
                    CANCEL
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Exercise Management - Show for Exercises tab */}
        {activeTab === 'exercises' && (
          <Card className="glass p-4 border border-stone-800">
            <div className="flex flex-col gap-3 mb-4">
              <h2 className="text-lg font-bold text-blood-400 tracking-wider">EXERCISE MANAGEMENT</h2>
              <Input
                type="text"
                placeholder="Search exercises..."
                value={exerciseSearch}
                onChange={(e) => setExerciseSearch(e.target.value)}
                className="bg-stone-900 border-stone-700 text-white"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={fetchAllExercises}
                  disabled={fetchingExercises}
                  size="sm"
                  className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold text-xs"
                >
                  {fetchingExercises ? 'Fetching...' : '🔄 Fetch All'}
                </Button>
                <Button
                  onClick={importYogaPilates}
                  disabled={fetchingExercises}
                  size="sm"
                  className="bg-gradient-to-r from-purple-700 to-purple-900 hover:from-purple-600 hover:to-purple-800 text-white font-bold text-xs"
                >
                  {fetchingExercises ? 'Importing...' : '🧘 Yoga/Pilates'}
                </Button>
                <Button
                  onClick={() => {
                    if ('caches' in window) {
                      caches.keys().then(names => {
                        names.forEach(name => caches.delete(name));
                      });
                      toast.success('Cache cleared! Please refresh the page.');
                    } else {
                      toast.info('Manual cache clear: Press Ctrl+Shift+R (or Cmd+Shift+R on Mac)');
                    }
                  }}
                  size="sm"
                  className="bg-orange-700 hover:bg-orange-600 text-white font-bold text-xs"
                >
                  🗑️ Clear Cache
                </Button>
              </div>
            </div>

            {/* Difficulty Filter */}
            <div className="mb-4 flex gap-2 flex-wrap">
              <button
                onClick={() => setDifficultyFilter('all')}
                className={`px-3 py-1 rounded text-xs font-bold ${difficultyFilter === 'all' ? 'bg-stone-700 text-white' : 'bg-stone-800 text-stone-400'}`}
              >
                ALL ({exercises.length})
              </button>
              <button
                onClick={() => setDifficultyFilter('beginner')}
                className={`px-3 py-1 rounded text-xs font-bold ${difficultyFilter === 'beginner' ? 'bg-green-700 text-white' : 'bg-stone-800 text-stone-400'}`}
              >
                BEGINNER ({exercises.filter(e => e.difficulty === 'beginner').length})
              </button>
              <button
                onClick={() => setDifficultyFilter('intermediate')}
                className={`px-3 py-1 rounded text-xs font-bold ${difficultyFilter === 'intermediate' ? 'bg-yellow-700 text-white' : 'bg-stone-800 text-stone-400'}`}
              >
                INTERMEDIATE ({exercises.filter(e => e.difficulty === 'intermediate').length})
              </button>
              <button
                onClick={() => setDifficultyFilter('advanced')}
                className={`px-3 py-1 rounded text-xs font-bold ${difficultyFilter === 'advanced' ? 'bg-red-700 text-white' : 'bg-stone-800 text-stone-400'}`}
              >
                ADVANCED ({exercises.filter(e => e.difficulty === 'advanced').length})
              </button>
              <button
                onClick={() => setDifficultyFilter('expert')}
                className={`px-3 py-1 rounded text-xs font-bold ${difficultyFilter === 'expert' ? 'bg-purple-700 text-white' : 'bg-stone-800 text-stone-400'}`}
              >
                EXPERT ({exercises.filter(e => e.difficulty === 'expert').length})
              </button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {exercises
                .filter(exercise => {
                  // Search filter
                  const matchesSearch = (exercise.name || '').toLowerCase().includes(exerciseSearch.toLowerCase()) ||
                    (exercise.muscle || '').toLowerCase().includes(exerciseSearch.toLowerCase()) ||
                    (exercise.equipment || '').toLowerCase().includes(exerciseSearch.toLowerCase());
                  
                  // Difficulty filter
                  const matchesDifficulty = difficultyFilter === 'all' || exercise.difficulty === difficultyFilter;
                  
                  return matchesSearch && matchesDifficulty;
                })
                .map((exercise) => (
                  <div key={exercise.id} className="glass p-4 rounded-lg border border-stone-800">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="text-blood-400 font-bold text-lg">{exercise.name}</p>
                          <span className={`text-xs px-2 py-1 rounded font-bold ${
                            exercise.difficulty === 'beginner' ? 'bg-green-700 text-white' :
                            exercise.difficulty === 'intermediate' ? 'bg-yellow-700 text-white' :
                            exercise.difficulty === 'advanced' ? 'bg-red-700 text-white' :
                            'bg-stone-700 text-white'
                          }`}>
                            {exercise.difficulty?.toUpperCase() || 'UNSET'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div>
                            <span className="text-stone-500">Muscle:</span>
                            <span className="text-white ml-1 font-bold">{exercise.muscle}</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Equipment:</span>
                            <span className="text-white ml-1 font-bold">{exercise.equipment}</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Type:</span>
                            <span className="text-white ml-1 font-bold">{exercise.type}</span>
                          </div>
                          <div>
                            <span className="text-stone-500">Source:</span>
                            <span className="text-blood-400 ml-1 font-bold">{exercise.source}</span>
                          </div>
                        </div>

                        {exercise.instructions && (
                          <p className="text-stone-400 text-sm mt-2 line-clamp-2">{exercise.instructions}</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openExerciseEditModal(exercise)}
                          className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold text-xs"
                        >
                          ✏️ EDIT FULL
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              
              {exercises.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-stone-400">No exercises loaded. Click FETCH ALL to load exercises from APIs, or YOGA/PILATES for yoga and stretching exercises.</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Data Export Tab */}
        {activeTab === 'export' && (
          <Card className="glass p-6 border border-stone-800">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Database className="w-8 h-8 text-blood-500" />
                <h2 className="text-xl font-bold text-blood-400 tracking-wider">DATA EXPORT</h2>
              </div>
              <p className="text-stone-400 text-sm">
                Export all user data directly to an external MongoDB database. This is useful for migrating data to your own production database.
              </p>
            </div>

            {/* MongoDB Connection Form */}
            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-stone-300 text-sm font-semibold mb-2 block">
                  MongoDB Connection URI *
                </Label>
                <Input
                  type="password"
                  placeholder="mongodb+srv://user:password@cluster.xxxxx.mongodb.net/"
                  value={mongodbUri}
                  onChange={(e) => setMongodbUri(e.target.value)}
                  className="bg-stone-900 border-stone-700 text-white font-mono text-sm"
                />
                <p className="text-stone-500 text-xs mt-1">
                  Your MongoDB Atlas connection string. Find it in Atlas → Connect → Connect your application
                </p>
              </div>

              <div>
                <Label className="text-stone-300 text-sm font-semibold mb-2 block">
                  Target Database Name
                </Label>
                <Input
                  type="text"
                  placeholder="anvilash_production"
                  value={targetDbName}
                  onChange={(e) => setTargetDbName(e.target.value)}
                  className="bg-stone-900 border-stone-700 text-white"
                />
                <p className="text-stone-500 text-xs mt-1">
                  The database name where data will be exported to
                </p>
              </div>
            </div>

            {/* Collections to Export */}
            <div className="mb-6">
              <h3 className="text-stone-300 font-semibold mb-3">Collections to Export:</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {[
                  'users', 'workout_logs', 'workout_plans', 'nutrition_logs',
                  'nutrition_goals', 'battles', 'exercise_history', 'favorite_exercises',
                  'fasting_logs', 'water_logs', 'weight_logs', 'meal_templates',
                  'friendships', 'friend_requests', 'invite_links', 'payment_transactions'
                ].map(col => (
                  <div key={col} className="flex items-center gap-2 text-stone-400">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>{col}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Export Button */}
            <Button
              onClick={handleExportToMongoDB}
              disabled={exporting || !mongodbUri}
              className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-800 hover:to-blood-950 text-white font-bold py-4 tracking-wider disabled:opacity-50"
            >
              {exporting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span> EXPORTING DATA...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2 inline" /> EXPORT TO MONGODB
                </>
              )}
            </Button>

            {/* Export Results */}
            {exportResults && (
              <div className={`mt-6 p-4 rounded-lg border ${
                exportResults.status === 'success' 
                  ? 'border-green-700 bg-green-900/20' 
                  : 'border-red-700 bg-red-900/20'
              }`}>
                <h3 className={`font-bold mb-3 ${
                  exportResults.status === 'success' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {exportResults.status === 'success' ? '✅ Export Successful!' : '❌ Export Failed'}
                </h3>
                
                {exportResults.status === 'success' ? (
                  <>
                    <p className="text-stone-300 mb-3">{exportResults.message}</p>
                    <p className="text-stone-400 text-sm mb-3">
                      Target Database: <span className="text-blood-400 font-mono">{exportResults.target_database}</span>
                    </p>
                    
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {Object.entries(exportResults.collections || {}).map(([name, result]) => (
                        <div key={name} className="flex justify-between items-center text-sm bg-stone-900/50 p-2 rounded">
                          <span className="text-stone-300 font-mono">{name}</span>
                          <span className={result.status === 'success' ? 'text-green-400' : result.status === 'empty' ? 'text-stone-500' : 'text-red-400'}>
                            {result.status === 'success' 
                              ? `+${result.inserted} / ~${result.updated} (${result.total} total)` 
                              : result.status === 'empty' 
                                ? 'Empty' 
                                : result.error}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-red-300">{exportResults.error}</p>
                )}
              </div>
            )}

            {/* Warning */}
            <div className="mt-6 p-4 bg-yellow-900/20 border border-yellow-700 rounded-lg">
              <p className="text-yellow-400 text-sm">
                <strong>⚠️ Important:</strong> This will export all data including user passwords (hashed) and sensitive information. 
                Make sure your target MongoDB is properly secured with authentication and network restrictions.
              </p>
            </div>
          </Card>
        )}

        {/* Downloads Tab - CSV Export */}
        {activeTab === 'downloads' && (
          <Card className="glass p-6 border border-stone-800" data-testid="downloads-panel">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <FileDown className="w-8 h-8 text-blood-500" />
                <h2 className="text-xl font-bold text-blood-400 tracking-wider">DOWNLOAD USER DATA</h2>
              </div>
              <p className="text-stone-400 text-sm">
                Download workout history, nutrition logs, and weight data as CSV files.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-stone-300 text-sm font-semibold mb-2 block">Select User</Label>
                <select
                  value={csvExportUser}
                  onChange={(e) => setCsvExportUser(e.target.value)}
                  className="w-full glass border border-stone-700 text-white px-4 py-3 rounded-lg bg-stone-900"
                  data-testid="csv-user-select"
                >
                  <option value="all">All Users</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username || u.email} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-stone-300 text-sm font-semibold mb-2 block">Data Type</Label>
                <select
                  value={csvDataType}
                  onChange={(e) => setCsvDataType(e.target.value)}
                  className="w-full glass border border-stone-700 text-white px-4 py-3 rounded-lg bg-stone-900"
                  data-testid="csv-data-type-select"
                >
                  <option value="all">All Data (Workouts + Nutrition + Weight)</option>
                  <option value="workouts">Workouts Only</option>
                  <option value="nutrition">Nutrition Only</option>
                  <option value="weight">Weight Logs Only</option>
                </select>
              </div>
            </div>

            <Button
              onClick={handleDownloadCsv}
              disabled={downloadingCsv}
              className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-800 hover:to-blood-950 text-white font-bold py-4 tracking-wider disabled:opacity-50"
              data-testid="download-csv-btn"
            >
              {downloadingCsv ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span> DOWNLOADING...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <FileDown className="w-5 h-5" /> DOWNLOAD CSV
                </span>
              )}
            </Button>

            <div className="mt-6 p-4 bg-stone-900/50 border border-stone-800 rounded-lg">
              <h3 className="text-stone-300 font-semibold mb-2 text-sm">What's included:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="flex items-start gap-2 text-stone-400">
                  <Dumbbell className="w-4 h-4 text-blood-400 mt-0.5 shrink-0" />
                  <span>Workout logs with exercises, sets, reps, and weight</span>
                </div>
                <div className="flex items-start gap-2 text-stone-400">
                  <TrendingUp className="w-4 h-4 text-blood-400 mt-0.5 shrink-0" />
                  <span>Nutrition logs with calories, protein, carbs, fat</span>
                </div>
                <div className="flex items-start gap-2 text-stone-400">
                  <Calendar className="w-4 h-4 text-blood-400 mt-0.5 shrink-0" />
                  <span>Weight tracking history with dates</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Password Reset Requests Tab */}
        {activeTab === 'password-resets' && (
          <Card className="glass p-6 border border-stone-800">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <KeyRound className="w-8 h-8 text-blood-500" />
                <h2 className="text-xl font-bold text-blood-400 tracking-wider">PASSWORD RESET REQUESTS</h2>
              </div>
              <p className="text-stone-400 text-sm">
                Review and approve password reset requests from users who forgot their passwords.
              </p>
            </div>

            {passwordResetRequests.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-stone-400">No pending password reset requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {passwordResetRequests.map((request) => (
                  <div key={request.id} className="bg-stone-900/50 border border-stone-700 rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <p className="text-white font-semibold">{request.username || 'Unknown'}</p>
                        <p className="text-stone-400 text-sm">{request.email}</p>
                        <div className="flex items-center gap-2 mt-1 text-stone-500 text-xs">
                          <Clock className="w-3 h-3" />
                          {new Date(request.created_at).toLocaleString()}
                        </div>
                      </div>
                      
                      {approvingRequest === request.id ? (
                        <div className="flex flex-col gap-2">
                          <Input
                            type="text"
                            placeholder="New password"
                            value={approvalPassword}
                            onChange={(e) => setApprovalPassword(e.target.value)}
                            className="bg-stone-800 border-stone-600 text-white text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleApprovePasswordReset(request.id)}
                              size="sm"
                              className="bg-green-700 hover:bg-green-800 text-white text-xs"
                            >
                              Confirm
                            </Button>
                            <Button
                              onClick={() => {
                                setApprovingRequest(null);
                                setApprovalPassword('');
                              }}
                              size="sm"
                              variant="outline"
                              className="border-stone-600 text-stone-400 text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setApprovingRequest(request.id)}
                            size="sm"
                            className="bg-green-700 hover:bg-green-800 text-white text-xs"
                          >
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectPasswordReset(request.id)}
                            size="sm"
                            className="bg-red-700 hover:bg-red-800 text-white text-xs"
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

      </div>

      {/* Exercise Edit Modal */}
      {exerciseEditModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 border-2 border-blood-700 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-blood-500 mb-6 tracking-wider">
              ⚔️ EDIT EXERCISE
            </h3>
            
            <div className="space-y-4">
              {/* Exercise Name */}
              <div>
                <label className="block text-stone-400 text-sm font-semibold mb-2">
                  Exercise Name
                </label>
                <input
                  type="text"
                  value={exerciseEditForm.name}
                  onChange={(e) => setExerciseEditForm({...exerciseEditForm, name: e.target.value})}
                  className="w-full bg-stone-800 border border-stone-700 text-white px-4 py-2 rounded focus:border-blood-700 focus:outline-none"
                  placeholder="e.g., Dumbbell Bicep Curl"
                />
              </div>

              {/* Muscle Group */}
              <div>
                <label className="block text-stone-400 text-sm font-semibold mb-2">
                  Muscle Group
                </label>
                <input
                  type="text"
                  value={exerciseEditForm.muscle}
                  onChange={(e) => setExerciseEditForm({...exerciseEditForm, muscle: e.target.value})}
                  className="w-full bg-stone-800 border border-stone-700 text-white px-4 py-2 rounded focus:border-blood-700 focus:outline-none"
                  placeholder="e.g., Biceps, Chest, Back"
                />
              </div>

              {/* Equipment */}
              <div>
                <label className="block text-stone-400 text-sm font-semibold mb-2">
                  Equipment Required
                </label>
                <input
                  type="text"
                  value={exerciseEditForm.equipment}
                  onChange={(e) => setExerciseEditForm({...exerciseEditForm, equipment: e.target.value})}
                  className="w-full bg-stone-800 border border-stone-700 text-white px-4 py-2 rounded focus:border-blood-700 focus:outline-none"
                  placeholder="e.g., Dumbbell, bodyweight, Bench"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Use: bodyweight, Dumbbell, Bench, resistance bands, etc. Avoid: cable, TRX, machine (for home workouts)
                </p>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-stone-400 text-sm font-semibold mb-2">
                  Difficulty Level
                </label>
                <select
                  value={exerciseEditForm.difficulty}
                  onChange={(e) => setExerciseEditForm({...exerciseEditForm, difficulty: e.target.value})}
                  className="w-full bg-stone-800 border border-stone-700 text-white px-4 py-2 rounded focus:border-blood-700 focus:outline-none"
                >
                  <option value="">Select Difficulty</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-stone-400 text-sm font-semibold mb-2">
                  Instructions / Form Tips
                </label>
                <textarea
                  value={exerciseEditForm.instructions}
                  onChange={(e) => setExerciseEditForm({...exerciseEditForm, instructions: e.target.value})}
                  className="w-full bg-stone-800 border border-stone-700 text-white px-4 py-2 rounded focus:border-blood-700 focus:outline-none h-24"
                  placeholder="Describe proper form and execution..."
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-stone-400 text-sm font-semibold mb-2">
                  Image URL (optional)
                </label>
                <input
                  type="text"
                  value={exerciseEditForm.image_url}
                  onChange={(e) => setExerciseEditForm({...exerciseEditForm, image_url: e.target.value})}
                  className="w-full bg-stone-800 border border-stone-700 text-white px-4 py-2 rounded focus:border-blood-700 focus:outline-none"
                  placeholder="https://..."
                />
              </div>

              {/* GIF URL */}
              <div>
                <label className="block text-stone-400 text-sm font-semibold mb-2">
                  GIF URL (optional)
                </label>
                <input
                  type="text"
                  value={exerciseEditForm.gif_url}
                  onChange={(e) => setExerciseEditForm({...exerciseEditForm, gif_url: e.target.value})}
                  className="w-full bg-stone-800 border border-stone-700 text-white px-4 py-2 rounded focus:border-blood-700 focus:outline-none"
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={saveExerciseEdit}
                className="flex-1 bg-blood-700 hover:bg-blood-800 text-white font-bold py-3 tracking-wider"
              >
                💾 SAVE CHANGES
              </Button>
              <Button
                onClick={() => {
                  setExerciseEditModal(null);
                  setExerciseEditForm({});
                }}
                variant="outline"
                className="flex-1 border-stone-700 text-stone-400 hover:bg-stone-800 font-semibold py-3"
              >
                ❌ CANCEL
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="glass p-8 max-w-md w-full border border-red-700">
            <h3 className="text-2xl font-bold text-red-400 mb-4 tracking-wider">
              ⚠️ CONFIRM BULK DELETE
            </h3>
            <p className="text-stone-400 mb-4">
              You are about to permanently delete <span className="text-red-400 font-bold">{selectedUsers.length} user(s)</span> and all their associated data:
            </p>
            <ul className="text-stone-400 text-sm mb-6 space-y-1 list-disc list-inside">
              <li>Workout plans and logs</li>
              <li>Nutrition logs</li>
              <li>Weight logs</li>
              <li>Recipes</li>
              <li>Progress photos</li>
              <li>Battle history</li>
              <li>Friend connections</li>
            </ul>
            <p className="text-red-400 font-bold mb-6">
              This action cannot be undone!
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={deletingUsers}
                className="flex-1 glass border-stone-700 text-stone-400 hover:bg-stone-800 font-semibold"
              >
                CANCEL
              </Button>
              <Button
                onClick={handleBulkDelete}
                disabled={deletingUsers}
                className="flex-1 bg-red-700 hover:bg-red-800 text-white font-bold tracking-wider"
              >
                {deletingUsers ? 'DELETING...' : 'DELETE ALL'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Admin;
